import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { ReportLayout } from '../reports/shared/ReportLayout';
import { getDateRangePresets } from '../../utils/reportHelpers';
import { apiClient } from '../../utils/apiClient';
import { SummaryCards } from './analytics/SummaryCards';
import { InvestigationsOverTimeChart } from './analytics/InvestigationsOverTimeChart';
import { RecurringFaultsSection } from './analytics/RecurringFaultsSection';
import type { AnalyticsData, AnalyticsSummary, Granularity, RecurringFaults } from './analytics/types';

// AI Fault Analytics page (sub-plan 19). Read-only. Consumes the TWO sub-plan-16 GET endpoints
// (summary — which embeds timeSeries — and recurring-faults) via apiClient. Gated upstream by
// <RequirePermission permission="ai_troubleshooting:manage:all"> in App.tsx. Vendor scope is fully
// automatic server-side, so the page ships only a date-range picker + a granularity toggle — no
// site/vendor filter (16 accepts no site_id/vendor_id, REF-08 §3.1).
export const AiFaultAnalytics: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();

  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [granularity, setGranularity] = useState<Granularity>('day');

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    const qs = new URLSearchParams({ from: startDate, to: endDate });
    const sumQs = new URLSearchParams(qs);
    sumQs.set('granularity', granularity);

    setLoading(true);
    setError(null);
    try {
      const [summary, recurring] = await Promise.all([
        apiClient.get<AnalyticsSummary>(
          `/api/backend/ai-troubleshooting/analytics/summary?${sumQs.toString()}`,
        ),
        apiClient.get<RecurringFaults>(
          `/api/backend/ai-troubleshooting/analytics/recurring-faults?${qs.toString()}`,
        ),
      ]);
      setData({ summary, recurring });
    } catch (e: any) {
      setError(
        e instanceof Error
          ? e
          : new Error(
              e?.message ??
                translate('aiTroubleshooting.analytics.errors.loadFailed', {
                  _: 'Failed to load analytics',
                }),
            ),
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, granularity, translate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const filters = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-end' },
        gap: { xs: 2, md: 3 },
        flexWrap: 'wrap',
      }}
    >
      <TextField
        type="date"
        size="small"
        label={translate('reports.filters.startDate', { _: 'Start date' })}
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: { xs: '100%', md: 180 } }}
      />
      <TextField
        type="date"
        size="small"
        label={translate('reports.filters.endDate', { _: 'End date' })}
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: { xs: '100%', md: 180 } }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {translate('aiTroubleshooting.analytics.granularity.label', { _: 'Granularity' })}
        </Typography>
        <ToggleButtonGroup
          value={granularity}
          exclusive
          size="small"
          onChange={(_, value) => value && setGranularity(value as Granularity)}
        >
          <ToggleButton value="day">
            {translate('aiTroubleshooting.analytics.granularity.day', { _: 'Day' })}
          </ToggleButton>
          <ToggleButton value="week">
            {translate('aiTroubleshooting.analytics.granularity.week', { _: 'Week' })}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Refresh />}
        onClick={fetchAnalytics}
        disabled={loading}
        sx={{ alignSelf: { xs: 'stretch', md: 'flex-end' } }}
      >
        {translate('reports.filters.refresh', { _: 'Refresh' })}
      </Button>
    </Box>
  );

  return (
    <ReportLayout
      title={translate('aiTroubleshooting.analytics.title', { _: 'AI Fault Analytics' })}
      description={translate('aiTroubleshooting.analytics.subtitle', {
        _: 'Investigation totals, trends over time, and recurring faults.',
      })}
      filters={filters}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          <SummaryCards summary={data.summary} translate={translate} />
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <InvestigationsOverTimeChart
              series={data.summary.timeSeries}
              granularity={data.summary.range?.granularity ?? granularity}
              isMobile={isMobile}
              translate={translate}
            />
          </Box>
          <RecurringFaultsSection
            recurring={data.recurring}
            isMobile={isMobile}
            translate={translate}
          />
        </Box>
      )}
    </ReportLayout>
  );
};

export default AiFaultAnalytics;
